import React, { useEffect, useState } from 'react';
import {
	Button,
	Box,
	FormControl,
	FormLabel,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Checkbox,
	useToast,
	Alert,
	AlertIcon,
	AlertTitle,
	Textarea,
} from '@chakra-ui/react';
import axios from 'axios';
import config from '../config';
import globalStore from '../store';
import { DataSelector, PlayerSegment, JsonDataSelectorField } from '@repo/alictus-common/types/segment';
import { SegmentationFilterOperation, JsonFilterOperation, JsonQueryValue } from '@repo/alictus-common/types/runtime/filter_operation';
import { FilterCondition, FilterType, OperatorSign, EVENT_NAMES } from '@repo/alictus-common/enums/filter_condition';
import { DateFilter } from '@repo/alictus-common/enums/date_filter';
import segmentationSchema from '@repo/alictus-common/validation/segmentation_schema';
import { calculateJsonAtaQueryResult } from './jsonata';
import { set } from 'lodash';
import { parse } from 'path';
import { Form } from 'formik';

const createEmptyUserSegment = (): PlayerSegment => {
	return {
		id: 0,
		uid: '',
		name: '',
		description: '',
		filterOperations: [],
		userId: 0,
		creationDate: 0,
		lastUpdated: 0,
		isTest: false,
		isDeleted: false,
		isOverWritten: false,
		isMock: false,
	};
};

export function SegmentModal({
	title,
	isOpen,
	onClose,
	segmentInstance,
	isAdminOrProduct,
}: {
	title: string;
	isOpen: boolean;
	onClose: () => void;
	segmentInstance?: PlayerSegment;
	isAdminOrProduct: boolean;
}) {
	let globalData = globalStore();
	const toast = useToast();
	const [playerSegment, setPlayerSegment] = useState<PlayerSegment>(createEmptyUserSegment());
	const [dataSelectors, setDataSelectors] = useState<DataSelector[]>([]);
	const [segmentFilter, setSegmentFilter] = useState<SegmentationFilterOperation>({
		Filter: FilterType.Segmentation,
		Condition: 0,
		Values: [],
		SelectorUid: '',
		KeyToFetch: '',
		DateFilter: 0,
	});
	const [jsonSegmentFilter, setJsonSegmentFilter] = useState<JsonFilterOperation>({
		Filter: FilterType.JSON,
		SelectorUid: '',
		QueryValues: [],
		Query: '',
	});

	const [showSubmitButton, setShowSubmitButton] = useState(true);
	const [isDateSelector, setIsDateSelector] = useState(false);
	const [isJsonQuery, setIsJsonQuery] = useState(false);
	const [isSqlQuery, setIsSqlQuery] = useState(false);
	const [fieldName, setFieldName] = useState('');
	const isMockCreator = globalData.user?.roles.includes('MOCK_CREATOR') || false;
	const [isMock, setIsMock] = useState(false);
	const [activeFilterType, setActiveFilterType] = useState<'json' | 'segment' | null>(null);
	const [isJsonFilterModalOpen, setIsJsonFilterModalOpen] = useState(false);
	const [playfabId, setPlayfabId] = useState<string>('');
	const [jsonFilterResult, setJsonFilterResult] = useState<null | boolean>(null);
	const [testMode, setTestMode] = useState<'playfab' | 'json'>('playfab');
	const [testJsonData, setTestJsonData] = useState<string>('');
	const [customEpochTime, setCustomEpochTime] = useState<string>('');
	const [customGmtOffset, setCustomGmtOffset] = useState<string>('');
	const [isTestingJsonFilter, setIsTestingJsonFilter] = useState(false);

	const fetchDataSelectors = async (): Promise<DataSelector[]> => {
		let bearerToken = 'Bearer ' + globalData.JWTToken;
		let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/selector';
		try {
			const response = await axios.get(url, { headers: { Authorization: bearerToken } });
			const selectors = response.data as DataSelector[];
			setDataSelectors(selectors);
			return selectors;
		} catch (error: any) {
			console.error('Error fetching data selectors:', error);
			setDataSelectors([]);
			return [];
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			if (isOpen) {
				const selectors = await fetchDataSelectors(); // Await the fetchDataSelectors function

				if (segmentInstance) {
					setPlayerSegment(segmentInstance);
					setIsMock(segmentInstance.isMock);

					const updatedFilterOperations = await Promise.all(
						segmentInstance.filterOperations.map(async (operation) => {
							if (operation.Filter === FilterType.JSON || operation.Filter === FilterType.Sticky) {
								const selector = selectors.find((s) => s.uid === operation.SelectorUid);
								if (!selector?.isJsonQuery) return operation;

								let parsedVariables: JsonQueryValue[] = [];
								let parsedQuery: string = '';

								try {
									const parsed = JSON.parse(selector.fieldName);
									parsedVariables = parsed.Variables || [];
									parsedQuery = parsed.Query || '';
								} catch (e) {
									console.error('Error parsing fieldName JSON:', e);
								}

								const existingMap = new Map((operation as JsonFilterOperation).QueryValues.map((qv) => [qv.QueryName, qv.Value]));

								const existingQuery = (operation as JsonFilterOperation).Query;

								const mergedQueryValues = parsedVariables.map((v) => ({
									UiName: v.UiName,
									Type: v.Type,
									QueryName: v.QueryName,
									Value: existingMap.get(v.QueryName) ?? null,
									DefaultValue: v.DefaultValue,
								}));
								return {
									...operation,
									QueryValues: mergedQueryValues,
									Query: existingQuery || parsedQuery,
								};
							}
							return operation;
						}),
					);

					setPlayerSegment((prevSegment) => ({
						...prevSegment,
						filterOperations: updatedFilterOperations,
					}));
				} else {
					setPlayerSegment(createEmptyUserSegment());
					setIsMock(isMockCreator);
				}
			}
		};

		fetchData();
	}, [isOpen, segmentInstance, isMockCreator]);

	const resetForm = () => {
		setPlayerSegment(createEmptyUserSegment());
		setDataSelectors([]);
		setSegmentFilter({
			Filter: FilterType.Segmentation,
			Condition: 0,
			Values: [],
			SelectorUid: '',
			KeyToFetch: '',
			DateFilter: 0,
		});
		setJsonSegmentFilter({
			Filter: FilterType.JSON,
			SelectorUid: '',
			QueryValues: [],
			Query: '',
		});
		// Add these for complete reset:
		setActiveFilterType(null);
		setTestJsonData('');
		setCustomEpochTime('');
		setCustomGmtOffset('');
		setTestMode('playfab');
		setJsonFilterResult(null);
		setPlayfabId('');
		setIsJsonFilterModalOpen(false);
		setIsTestingJsonFilter(false);
	};

	function updatePlayerSegment(playerSegmentField: Partial<PlayerSegment>) {
		setPlayerSegment({ ...playerSegment, ...playerSegmentField });
	}

	function addFilterOperation(filterOperation: SegmentationFilterOperation | JsonFilterOperation) {
		let newFilterOperations = [...playerSegment.filterOperations, filterOperation];
		setPlayerSegment({ ...playerSegment, filterOperations: newFilterOperations });
	}

	function removeFilterOperation(index: number) {
		let newFilterOperations = playerSegment.filterOperations.filter((_, i) => i !== index);
		setPlayerSegment({ ...playerSegment, ...{ filterOperations: newFilterOperations } });
	}

	function updateFilterOperation(index: number, filterOperationField: Partial<SegmentationFilterOperation | JsonFilterOperation>) {
		//console.log('Update Filter Operation', index, filterOperationField);

		// Parse the Values field. If it contains a comma, split it into an array
		// Handle SegmentationFilterOperation-specific logic
		if ('Values' in filterOperationField) {
			if (filterOperationField.Values) {
				const values = filterOperationField.Values.toString()
					.split(',')
					.map((value) => value.trim());
				filterOperationField.Values = values;
			}
		}

		if (index === -1) {
			// Safely update the current filter (segment or JSON)
			if (filterOperationField.Filter === FilterType.JSON ||  filterOperationField.Filter === FilterType.Sticky || 'QueryValues' in filterOperationField) {
				setJsonSegmentFilter((prevJsonSegmentFilter) => ({
					...prevJsonSegmentFilter,
					...filterOperationField,
				}));
			} else {
				setSegmentFilter((prevSegmentFilter) => ({
					...prevSegmentFilter,
					...filterOperationField,
				}));
			}
			return;
		}

		// Update an existing filter operation in the playerSegment
		setPlayerSegment((prevPlayerSegment) => {
			const existing = prevPlayerSegment.filterOperations[index];
			const incomingType = filterOperationField.Filter ?? existing.Filter;

			let updated: SegmentationFilterOperation | JsonFilterOperation;

			            // Check if this is actually a type switch between fundamentally different types
            const isActualTypeSwitch = (
                (existing.Filter === FilterType.Segmentation && (incomingType === FilterType.JSON || incomingType === FilterType.Sticky)) ||
                ((existing.Filter === FilterType.JSON || existing.Filter === FilterType.Sticky) && incomingType === FilterType.Segmentation)
            );

            if (incomingType === FilterType.JSON || incomingType === FilterType.Sticky) {
                if (isActualTypeSwitch) {
                    // Only reset when switching from Segmentation to JSON/Sticky
                    updated = {
                        Filter: FilterType.JSON, // Always use JSON internally, convert to Sticky on submit
                        SelectorUid: filterOperationField.SelectorUid ?? '',
                        QueryValues: 'QueryValues' in filterOperationField ? (filterOperationField.QueryValues ?? []) : [],
                        Query: 'Query' in filterOperationField ? (filterOperationField.Query ?? '') : '',
                    };
                } else {
                    // Preserve existing data when switching between JSON and Sticky or updating fields
                    updated = {
                        ...(existing as JsonFilterOperation),
                        Filter: FilterType.JSON, // Always use JSON internally
                        ...(filterOperationField as Partial<JsonFilterOperation>),
                    };
                }
            } else {
                if (isActualTypeSwitch) {
                    // Only reset when switching from JSON/Sticky to Segmentation
                    updated = {
                        Filter: FilterType.Segmentation,
                        SelectorUid: filterOperationField.SelectorUid ?? '',
                        Condition: 0,
                        Values: [],
                        KeyToFetch: '',
                        DateFilter: 0,
                    };
                } else {
                    // Preserve existing data when updating segmentation fields
                    updated = {
                        ...(existing as SegmentationFilterOperation),
                        ...(filterOperationField as Partial<SegmentationFilterOperation>),
                    };
                }
            }

			const updatedFilterOperations = [...prevPlayerSegment.filterOperations];
			updatedFilterOperations[index] = updated;

			/* 			// Type guard for SegmentationFilterOperation
			if (updatedFilterOperations[index].Filter === FilterType.Segmentation) {
				updatedFilterOperations[index] = {
					...updatedFilterOperations[index],
					...(filterOperationField as Partial<SegmentationFilterOperation>),
				};
			}

			// Type guard for JsonFilterOperation
			if (updatedFilterOperations[index].Filter === FilterType.JSON) {
				updatedFilterOperations[index] = {
					...updatedFilterOperations[index],
					...(filterOperationField as Partial<JsonFilterOperation>),
				};
			} */
			return {
				...prevPlayerSegment,
				filterOperations: updatedFilterOperations,
			};
		});
	}

	function updateFilterCondition(index: number, condition: number): void {
		console.log('Update Filter Condition', index, condition);
		if (index === -1) {
			setSegmentFilter({ ...segmentFilter, ...{ Condition: condition as unknown as FilterCondition } });
			return;
		}
		console.log('Updating already added filter condition', condition);
		let filterOp = playerSegment.filterOperations;
		// Check if the filter operation is of type SegmentationFilterOperation
		if ('Condition' in filterOp[index]) {
			(filterOp[index] as SegmentationFilterOperation).Condition = condition as unknown as FilterCondition;
			setPlayerSegment({ ...playerSegment, filterOperations: filterOp });
		} else {
			console.error('Cannot update Condition for JsonFilterOperation');
		}
	}

	const handleSubmit = async (event?: React.FormEvent) => {
		if (event) event.preventDefault();
		setShowSubmitButton(false);
		console.log('Validating Segment', playerSegment);
		const validationResult = segmentationSchema.safeParse(playerSegment);
		if (!validationResult.success) {
			console.error('Validation failed:', validationResult.error.issues);

			// Prepare the validation error messages
			const errorMessages = validationResult.error.issues.map((issue) => `${issue.message}`);

			// Show error toast with line breaks
			toast({
				title: 'Validation Error',
				description: (
					<Box>
						{errorMessages.map((msg, idx) => (
							<Box key={idx}>- {msg}</Box>
						))}
					</Box>
				),
				status: 'error',
				duration: 7000,
				isClosable: true,
				position: 'top',
			});

			setShowSubmitButton(true);
			return;
		}

		// Show success toast
		toast({
			title: 'Validation Passed',
			description: 'The configuration has been validated successfully.',
			status: 'success',
			duration: 3000,
			isClosable: true,
			position: 'top',
		});

		setShowSubmitButton(true);
		console.log('Validation passed');

		// Convert sticky selectors to sticky type before sending
		const processedSegment = {
			...playerSegment,
			filterOperations: playerSegment.filterOperations.map((operation) => {
				if (operation.Filter === FilterType.JSON) {
					const selector = dataSelectors.find((s) => s.uid === operation.SelectorUid);
					if (selector?.isSticky) {
						return {
							...operation,
							Filter: FilterType.Sticky,
						};
					}
				}
				return operation;
			}),
		};

		if (segmentInstance) {
			await updateSegmentInstanceRequest(processedSegment);
		} else {
			await createNewSegmentInstanceRequest(processedSegment);
		}
	};

	const createNewSegmentInstanceRequest = async (segmentToSend = playerSegment) => {
		try {
			console.log('Creating Segment', segmentToSend);
			setShowSubmitButton(false);
			segmentToSend.isMock = isMock;
			console.log('Player Segment', JSON.stringify(segmentToSend, null, 2));
			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/segment';
			await axios.post(url, segmentToSend, { headers: { Authorization: 'Bearer ' + globalData.JWTToken } }).then((response) => {
				console.log(response);
				handleClose();
			});
		} catch (error: any) {
			console.error(error);
			toast({
				title: 'Error Creating Segment',
				description: error.response?.data,
				status: 'error',
				duration: 10000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setShowSubmitButton(true);
		}
	};

	const updateSegmentInstanceRequest = async (segmentToSend = playerSegment) => {
		try {
			setShowSubmitButton(false);
			console.log('Player Segment', JSON.stringify(segmentToSend, null, 2));

			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + `/segment/${segmentInstance?.id}`;
			await axios.put(url, segmentToSend, { headers: { Authorization: 'Bearer ' + globalData.JWTToken } }).then((response) => {
				console.log(response);
				handleClose();
			});
		} catch (error: any) {
			console.error(error);
			toast({
				title: 'Error Updating Selector',
				description: error.response?.data,
				status: 'error',
				duration: 10000,
				isClosable: true,
				position: 'top',
			});
		} finally {
			setShowSubmitButton(true);
		}
	};

	const handleClose = () => {
		setShowSubmitButton(false);
		resetForm();
		onClose();
	};

	const addSegmentFilter = () => {
		if (activeFilterType === 'segment') {
			addFilterOperation(segmentFilter);
			setSegmentFilter({
				Filter: FilterType.Segmentation,
				Condition: 0,
				Values: [],
				SelectorUid: '',
				KeyToFetch: '',
				DateFilter: 0,
			});
		} else if (activeFilterType === 'json') {
			addFilterOperation(jsonSegmentFilter);
			setJsonSegmentFilter({
				Filter: FilterType.JSON,
				SelectorUid: '',
				QueryValues: [],
				Query: '',
			});
		}
		setActiveFilterType(null); // Reset active filter type after adding
	};

	const removeSegmentFilter = (index: number) => {
		removeFilterOperation(index);
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose}>
			<ModalOverlay />
			<ModalContent maxW="950px" border={'1px'} borderColor={'gray.700'}>
				<ModalHeader>{title}</ModalHeader>
				<ModalCloseButton />
				<form onSubmit={handleSubmit}>
					<ModalBody>
						<Stack spacing={4}>
							<FormControl isRequired>
								<FormLabel>Name</FormLabel>
								<Input value={playerSegment.name} onChange={(event) => updatePlayerSegment({ name: event.target.value })} />
							</FormControl>
							<FormControl isRequired>
								<FormLabel>Description</FormLabel>
								<Input value={playerSegment.description} onChange={(event) => updatePlayerSegment({ description: event.target.value })} />
							</FormControl>
							{playerSegment.filterOperations.map((playerSegmentFilterOperation, index) => (
								<HStack spacing={4}>
									<Select
										placeholder="Select Selector"
										value={playerSegmentFilterOperation.SelectorUid}
										onChange={(e) => {
											const selectedUid = e.target.value;
											const selectedSelector = dataSelectors.find((selector) => selector.uid === selectedUid);
											let parsedVariables: JsonQueryValue[] = [];
											let parsedQuery: string = '';
											if (!selectedSelector) return;
											if (selectedSelector.isJsonQuery) {
												try {
													const parsed = JSON.parse(selectedSelector.fieldName || '{}');
													parsedVariables = parsed.Variables || [];
													parsedQuery = parsed.Query || '';
												} catch (err) {
													console.error('Error parsing fieldName JSON:', err);
												}

												                                                // Check if we're switching back to the same selector to preserve existing query
                                                const existingOperation = playerSegmentFilterOperation as JsonFilterOperation;
                                                const shouldPreserveQuery = existingOperation.SelectorUid === selectedUid && existingOperation.Query;
												
												updateFilterOperation(index, {
													Filter: FilterType.JSON,
													SelectorUid: selectedUid,
                                                    Query: shouldPreserveQuery ? existingOperation.Query : parsedQuery,
													QueryValues: parsedVariables.map((variable: JsonQueryValue) => ({
														UiName: variable.UiName,
														Type: variable.Type,
														QueryName: variable.QueryName,
														Value: null,
														DefaultValue: variable.DefaultValue,
													})),
												});
											} else {
												updateFilterOperation(index, {
													Filter: FilterType.Segmentation,
													SelectorUid: selectedUid,
													Condition: 0,
													Values: [],
													KeyToFetch: '',
													DateFilter: 0,
												});
											}
										}}
									>
										{dataSelectors.map((selector) => (
											<option key={selector.uid} value={selector.uid}>
												{selector.name}
											</option>
										))}
									</Select>
									{/* Type-specific logic */}
									{playerSegmentFilterOperation.Filter === FilterType.Segmentation && (
										<>
											<Select
												placeholder="Select Condition"
												value={(playerSegmentFilterOperation as SegmentationFilterOperation).Condition}
												onChange={(e) => updateFilterCondition(index, parseInt(e.target.value))}
											>
												{Object.entries(FilterCondition)
													.filter(([key, condition]) => isNaN(Number(key))) // Filter out numeric keys
													.map(([key, condition]) => (
														<option key={key} value={condition}>
															{key}
														</option>
													))}
											</Select>
											{(dataSelectors.find((selector) => selector.uid === playerSegmentFilterOperation.SelectorUid)?.isDate || false) && (
												<Select
													//placeholder="Select Date Filter"
													value={(playerSegmentFilterOperation as SegmentationFilterOperation).DateFilter}
													onChange={(e) => updateFilterOperation(index, { DateFilter: parseInt(e.target.value) })}
												>
													{Object.entries(DateFilter)
														.filter(([key, condition]) => isNaN(Number(key))) // Filter out numeric keys
														.map(([key, condition]) => (
															<option key={key} value={condition} disabled={key === 'MinutesSince'}>
																{key}
															</option>
														))}
												</Select>
											)}
											<Input
												value={(playerSegmentFilterOperation as SegmentationFilterOperation).Values.toString()}
												onChange={(e) => updateFilterOperation(index, { Values: [e.target.value] })}
												placeholder="Filter Value"
											/>
										</>
									)}
									{(playerSegmentFilterOperation.Filter === FilterType.JSON || playerSegmentFilterOperation.Filter === FilterType.Sticky) && (
										<>
											{/* Render inputs for each JsonQueryValue */}
											{(playerSegmentFilterOperation as JsonFilterOperation).QueryValues.map((queryValue, queryIndex) => {
												if (
													queryValue.Type.toLowerCase() === 'number' ||
													queryValue.Type.toLowerCase() === 'dayssince' ||
													queryValue.Type.toLowerCase() === 'hourssince'
												) {
													// Render number input
													return (
														<Input
															key={queryIndex}
															type="number" // Number input
															placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
															value={queryValue.Value?.toString() || ''} // Display the current value
															onChange={(e) => {
																const updatedQueryValues = [...(playerSegmentFilterOperation as JsonFilterOperation).QueryValues];
																updatedQueryValues[queryIndex] = { ...queryValue, Value: parseFloat(e.target.value) || null }; // Parse as number
																const newValue = e.target.value === '' ? null : parseFloat(e.target.value); // Handle empty input
																updatedQueryValues[queryIndex] = { ...queryValue, Value: newValue };
																updateFilterOperation(index, { QueryValues: updatedQueryValues });
															}}
														/>
													);
												} else if (queryValue.Type.toLowerCase() === 'boolean') {
													// Render boolean dropdown
													return (
														<Select
															key={queryIndex}
															placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
															value={queryValue.Value?.toString() || ''} // Display the current value
															onChange={(e) => {
																const updatedQueryValues = [...(playerSegmentFilterOperation as JsonFilterOperation).QueryValues];
																updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value === 'true' }; // Parse as boolean
																updateFilterOperation(index, { QueryValues: updatedQueryValues });
															}}
														>
															<option value="true">True</option>
															<option value="false">False</option>
														</Select>
													);
												} else if (queryValue.Type.toLowerCase() === 'operator') {
													// Render operator dropdown
													return (
														<Select
															key={queryIndex}
															placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
															value={queryValue.Value?.toString() || ''} // Display the current operator
															onChange={(e) => {
																const updatedQueryValues = [...(playerSegmentFilterOperation as JsonFilterOperation).QueryValues];
																updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value }; // Update operator value
																updateFilterOperation(index, { QueryValues: updatedQueryValues });
															}}
														>
															{Object.entries(OperatorSign).map(([name, operator]) => (
																<option key={operator} value={operator}>
																	{name} {/* Show name like Equal, NotEqual, etc. */}
																</option>
															))}
														</Select>
													);
												} else if (queryValue.Type.toLowerCase() === 'eventname') {
													return (
														<Select
															key={queryIndex}
															placeholder={queryValue.UiName}
															value={queryValue.Value?.toString() || ''}
															onChange={(e) => {
																const updatedQueryValues = [...(playerSegmentFilterOperation as JsonFilterOperation).QueryValues];
																updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value };
																updateFilterOperation(index, { QueryValues: updatedQueryValues });
															}}
														>
															{EVENT_NAMES.map((eventName) => (
																<option key={eventName} value={eventName}>
																	{eventName}
																</option>
															))}
														</Select>
													);
												} else {
													// Render string input (default)
													return (
														<Input
															key={queryIndex}
															type="text" // Text input
															placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
															value={queryValue.Value?.toString() || ''} // Display the current value
															onChange={(e) => {
																const updatedQueryValues = [...(playerSegmentFilterOperation as JsonFilterOperation).QueryValues];
																updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value }; // Parse as string
																updateFilterOperation(index, { QueryValues: updatedQueryValues });
															}}
														/>
													);
												}
											})}
										</>
									)}
									<Button width={'350px'} minW={'115px'} colorScheme="red" onClick={() => removeFilterOperation(index)}>
										Remove Filter
									</Button>
								</HStack>
							))}
							<HStack spacing={4}>
								{/* Shared "Select Selector" part */}
								<Select
									placeholder="Select Selector"
									value={activeFilterType === 'json' ? jsonSegmentFilter.SelectorUid : segmentFilter.SelectorUid} // Directly use SelectorUid from either filter
									onChange={(e) => {
										const selectedUid = e.target.value;
										const selectedSelector = dataSelectors.find((selector) => selector.uid === selectedUid);

										if (!selectedUid) {
											// Reset activeFilterType and filters when no selection is made
											setActiveFilterType(null);
											setSegmentFilter({
												Filter: FilterType.Segmentation,
												Condition: 0,
												Values: [],
												SelectorUid: '',
												KeyToFetch: '',
												DateFilter: 0,
											});
											setJsonSegmentFilter({
												Filter: FilterType.JSON,
												SelectorUid: '',
												QueryValues: [],
												Query: '',
											});
											setIsDateSelector(false);
											return;
										}

										if (selectedSelector?.isJsonQuery) {
											setActiveFilterType('json');
											// Parse fieldName only when the selector isJsonQuery
											let parsedVariables: JsonQueryValue[] = [];
											let parsedFieldName: JsonDataSelectorField = { Variables: [], Query: '' };
											try {
												parsedFieldName = JSON.parse(selectedSelector.fieldName); // Parse fieldName JSON
												parsedVariables = parsedFieldName.Variables || []; // Extract Variables array
											} catch (error) {
												console.error('Error parsing fieldName JSON:', error);
											}
											setSegmentFilter({
												Filter: FilterType.Segmentation,
												Condition: 0,
												Values: [],
												SelectorUid: '',
												KeyToFetch: '',
												DateFilter: 0,
											});
											// Update JSON filter and reset Segmentation filter
											setJsonSegmentFilter({
												Filter: FilterType.JSON,
												SelectorUid: selectedUid,
												QueryValues: parsedVariables.map((variable) => ({
													UiName: variable.UiName,
													Type: variable.Type,
													QueryName: variable.QueryName,
													Value: null,
													DefaultValue: variable.DefaultValue,
												})),
												Query: parsedFieldName.Query || '', // Use Query from parsed fieldName
											});
										} else {
											setActiveFilterType('segment');

											// Update Segmentation filter and reset JSON filter
											setJsonSegmentFilter({
												Filter: FilterType.JSON,
												SelectorUid: '',
												QueryValues: [],
												Query: '',
											});
											setSegmentFilter({
												Filter: FilterType.Segmentation,
												SelectorUid: selectedUid,
												Condition: 0,
												Values: [],
												KeyToFetch: '',
												DateFilter: 0,
											});

											setIsDateSelector(selectedSelector?.isDate || false);
										}
									}}
									minW="180px"
								>
									{dataSelectors.map((selector) => (
										<option key={selector.uid} value={selector.uid}>
											{selector.name}
										</option>
									))}
								</Select>

								{/* Render inputs dynamically based on the selected FilterType */}
								{(activeFilterType === 'segment' || activeFilterType === null) && (
									<>
										<Select
											placeholder="Select Condition"
											value={segmentFilter.Condition}
											onChange={(e) => updateFilterCondition(-1, parseInt(e.target.value))}
										>
											{Object.entries(FilterCondition)
												.filter(([key, condition]) => isNaN(Number(key))) // Filter out numeric keys
												.map(([key, condition]) => (
													<option key={key} value={condition}>
														{key}
													</option>
												))}
										</Select>
										{isDateSelector && (
											<Select
												value={segmentFilter.DateFilter}
												onChange={(e) => updateFilterOperation(-1, { DateFilter: parseInt(e.target.value) })}
											>
												{Object.entries(DateFilter)
													.filter(([key, condition]) => isNaN(Number(key))) // Filter out numeric keys
													.map(([key, condition]) => (
														<option key={key} value={condition} disabled={key === 'MinutesSince'}>
															{key}
														</option>
													))}
											</Select>
										)}
										<Input
											value={segmentFilter.Values.toString()}
											onChange={(e) => updateFilterOperation(-1, { Values: [e.target.value] })}
											placeholder="Filter Value"
										/>
									</>
								)}

								{activeFilterType === 'json' && (
									<>
										{jsonSegmentFilter.QueryValues.map((queryValue, queryIndex) => {
											if (
												queryValue.Type.toLowerCase() === 'number' ||
												queryValue.Type.toLowerCase() === 'dayssince' ||
												queryValue.Type.toLowerCase() === 'hourssince'
											) {
												// Render number input
												return (
													<Input
														key={queryIndex}
														type="number" // Number input
														placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
														value={queryValue.Value?.toString() || ''} // Display the current value
														onChange={(e) => {
															const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
															const newValue = e.target.value === '' ? null : parseFloat(e.target.value); // Handle empty input and preserve 0
															updatedQueryValues[queryIndex] = { ...queryValue, Value: newValue };
															setJsonSegmentFilter((prevJsonSegmentFilter) => ({
																...prevJsonSegmentFilter,
																QueryValues: updatedQueryValues,
															}));
														}}
													/>
												);
											} else if (queryValue.Type.toLowerCase() === 'boolean') {
												// Render boolean dropdown
												return (
													<Select
														key={queryIndex}
														placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
														value={queryValue.Value?.toString() || ''} // Display the current value
														onChange={(e) => {
															const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
															updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value === 'true' }; // Parse as boolean
															setJsonSegmentFilter((prevJsonSegmentFilter) => ({
																...prevJsonSegmentFilter,
																QueryValues: updatedQueryValues,
															}));
														}}
													>
														<option value="true">True</option>
														<option value="false">False</option>
													</Select>
												);
											} else if (queryValue.Type.toLowerCase() === 'operator') {
												// Render operator dropdown
												return (
													<Select
														key={queryIndex}
														placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
														value={queryValue.Value?.toString() || ''} // Display the current operator
														onChange={(e) => {
															const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
															updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value }; // Update operator value
															setJsonSegmentFilter((prevJsonSegmentFilter) => ({
																...prevJsonSegmentFilter,
																QueryValues: updatedQueryValues,
															}));
														}}
													>
														{Object.entries(OperatorSign).map(([name, operator]) => (
															<option key={operator} value={operator}>
																{name} {/* Show name like Equal, NotEqual, etc. */}
															</option>
														))}
													</Select>
												);
											} else if (queryValue.Type.toLowerCase() === 'eventname') {
												// Render event name dropdown
												return (
													<Select
														key={queryIndex}
														placeholder={queryValue.UiName}
														value={queryValue.Value?.toString() || ''}
														onChange={(e) => {
															const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
															updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value };
															setJsonSegmentFilter((prevJsonSegmentFilter) => ({
																...prevJsonSegmentFilter,
																QueryValues: updatedQueryValues,
															}));
														}}
													>
														{EVENT_NAMES.map((eventName) => (
															<option key={eventName} value={eventName}>
																{eventName}
															</option>
														))}
													</Select>
												);
											} else {
												// Render string input (default)
												return (
													<Input
														key={queryIndex}
														type="text" // Text input
														placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
														value={queryValue.Value?.toString() || ''} // Display the current value
														onChange={(e) => {
															const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
															updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value }; // Parse as string
															setJsonSegmentFilter((prevJsonSegmentFilter) => ({
																...prevJsonSegmentFilter,
																QueryValues: updatedQueryValues,
															}));
														}}
													/>
												);
											}
										})}
									</>
								)}
								<Button width={'350px'} minW={'115px'} colorScheme="blue" onClick={addSegmentFilter}>
									Add Filter
								</Button>
							</HStack>
						</Stack>
						<ModalFooter padding={'20px'}>
							<HStack justifyContent="space-between" alignItems="center" width="100%">
								<Checkbox
									isDisabled={!isMockCreator}
									isChecked={isMock}
									onChange={() => {
										const newIsMock = !isMock; // Calculate the new value
										setIsMock(newIsMock); // Update local state
										updatePlayerSegment({ isMock: newIsMock }); // Use the new value
									}}
								>
									Is Mock
								</Checkbox>

								<HStack spacing={3}>
									<Button
										colorScheme="teal"
										variant="outline"
										onClick={() => {
											setIsJsonFilterModalOpen(true);
										}}
									>
										{' '}
										Open JSON Filter Editor{' '}
									</Button>
									<Button variant="outline" onClick={onClose}>
										Cancel
									</Button>
									{segmentInstance ? (
										<Button colorScheme="blue" isLoading={!showSubmitButton} type="submit" isDisabled={!isAdminOrProduct}>
											Update
										</Button>
									) : (
										<Button colorScheme="blue" isLoading={!showSubmitButton} type="submit" isDisabled={!isAdminOrProduct}>
											Create
										</Button>
									)}
								</HStack>
							</HStack>
						</ModalFooter>
					</ModalBody>
				</form>
			</ModalContent>
			<Modal
				isOpen={isJsonFilterModalOpen}
				onClose={() => {
					setIsJsonFilterModalOpen(false);
					setPlayfabId('');
					//setTestJsonData('');
					setCustomEpochTime('');
					setCustomGmtOffset('');
					setTestMode('playfab');
					/* 	setJsonSegmentFilter({
						Filter: FilterType.JSON,
						SelectorUid: '',
						QueryValues: [],
						Query: '',
					}); */
					setJsonFilterResult(null);
					// setActiveFilterType(null);
				}} /* size="xl" */
				size="xl"
			>
				<ModalOverlay />
				<ModalContent maxW="1000px" maxH="90vh" display="flex" flexDirection="column">
					<ModalHeader>JSON Filter Editor</ModalHeader>
					<ModalCloseButton />
					<ModalBody flex="1" overflowY="auto" maxH="70vh">
						<Stack spacing={4}>
							<Select
								placeholder="Select Selector"
								value={jsonSegmentFilter.SelectorUid}
								onChange={(e) => {
									const selectedUid = e.target.value;
									const selectedSelector = dataSelectors.find((selector) => selector.uid === selectedUid);
									if (!selectedSelector) return;
									let parsedVariables: JsonQueryValue[] = [];

									let parsedFieldName: JsonDataSelectorField = { Variables: [], Query: '' };

									try {
										parsedFieldName = JSON.parse(selectedSelector.fieldName); // Parse fieldName JSON
										parsedVariables = parsedFieldName.Variables || []; // Extract Variables array
									} catch (error) {
										console.error('Error parsing fieldName JSON:', error);
									}
									// Update JSON filter and reset Segmentation filter
									setJsonSegmentFilter({
										Filter: FilterType.JSON,
										SelectorUid: selectedUid,
										QueryValues: parsedVariables.map((variable) => ({
											UiName: variable.UiName,
											Type: variable.Type,
											QueryName: variable.QueryName,
											Value: null,
											DefaultValue: variable.DefaultValue,
										})),
										Query: parsedFieldName.Query || '', // Use Query from parsed fieldName
									});
								}}
							>
								{dataSelectors
									.filter((selector) => selector.isJsonQuery)
									.map((selector) => (
										<option key={selector.uid} value={selector.uid}>
											{selector.name}
										</option>
									))}
							</Select>
							{jsonSegmentFilter.QueryValues.map((queryValue, queryIndex) => {
								if (
									queryValue.Type.toLowerCase() === 'number' ||
									queryValue.Type.toLowerCase() === 'dayssince' ||
									queryValue.Type.toLowerCase() === 'hourssince'
								) {
									// Render number input
									return (
										<Input
											key={queryIndex}
											type="number" // Number input
											placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
											value={queryValue.Value?.toString() || ''} // Display the current value
											onChange={(e) => {
												const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
												const newValue = e.target.value === '' ? null : parseFloat(e.target.value); // Handle empty input and preserve 0
												updatedQueryValues[queryIndex] = { ...queryValue, Value: newValue };
												setJsonSegmentFilter((prevJsonSegmentFilter) => ({
													...prevJsonSegmentFilter,
													QueryValues: updatedQueryValues,
												}));
											}}
										/>
									);
								} else if (queryValue.Type.toLowerCase() === 'boolean') {
									// Render boolean dropdown
									return (
										<Select
											key={queryIndex}
											placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
											value={queryValue.Value?.toString() || ''} // Display the current value
											onChange={(e) => {
												const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
												updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value === 'true' }; // Parse as boolean
												setJsonSegmentFilter((prevJsonSegmentFilter) => ({
													...prevJsonSegmentFilter,
													QueryValues: updatedQueryValues,
												}));
											}}
										>
											<option value="true">True</option>
											<option value="false">False</option>
										</Select>
									);
								} else if (queryValue.Type.toLowerCase() === 'operator') {
									// Render operator dropdown
									return (
										<Select
											key={queryIndex}
											placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
											value={queryValue.Value?.toString() || ''} // Display the current operator
											onChange={(e) => {
												const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
												updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value }; // Update operator value
												setJsonSegmentFilter((prevJsonSegmentFilter) => ({
													...prevJsonSegmentFilter,
													QueryValues: updatedQueryValues,
												}));
											}}
										>
											{Object.entries(OperatorSign).map(([name, operator]) => (
												<option key={operator} value={operator}>
													{name} {/* Show name like Equal, NotEqual, etc. */}
												</option>
											))}
										</Select>
									);
								} else if (queryValue.Type.toLowerCase() === 'eventname') {
									// Render event name dropdown
									return (
										<Select
											key={queryIndex}
											placeholder={queryValue.UiName}
											value={queryValue.Value?.toString() || ''}
											onChange={(e) => {
												const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
												updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value };
												setJsonSegmentFilter((prevJsonSegmentFilter) => ({
													...prevJsonSegmentFilter,
													QueryValues: updatedQueryValues,
												}));
											}}
										>
											{EVENT_NAMES.map((eventName) => (
												<option key={eventName} value={eventName}>
													{eventName}
												</option>
											))}
										</Select>
									);
								} else {
									// Render string input (default)
									return (
										<Input
											key={queryIndex}
											type="text" // Text input
											placeholder={queryValue.UiName} // Use JsonQueryValue.UiName for placeholder
											value={queryValue.Value?.toString() || ''} // Display the current value
											onChange={(e) => {
												const updatedQueryValues = [...jsonSegmentFilter.QueryValues];
												updatedQueryValues[queryIndex] = { ...queryValue, Value: e.target.value }; // Parse as string
												setJsonSegmentFilter((prevJsonSegmentFilter) => ({
													...prevJsonSegmentFilter,
													QueryValues: updatedQueryValues,
												}));
											}}
										/>
									);
								}
							})}
							<FormControl>
								<FormLabel>Test Mode</FormLabel>
								<Select
									value={testMode}
									onChange={(e) => {
										setTestMode(e.target.value as 'playfab' | 'json');
										setJsonFilterResult(null); // Reset result when changing test mode
									}}
								>
									<option value="playfab">Test with Playfab ID</option>
									<option value="json">Test with JSON Data</option>
								</Select>
							</FormControl>
							{testMode === 'playfab' && (
								<FormControl isRequired>
									<FormLabel>Playfab ID</FormLabel>
									<Input value={playfabId} onChange={(event) => setPlayfabId(event.target.value)} placeholder="Enter Playfab ID to test" />
								</FormControl>
							)}
							{testMode === 'json' && (
								<>
									<FormControl isRequired>
										<FormLabel>Test Json Data</FormLabel>
										<Textarea
											value={testJsonData}
											onChange={(event) => setTestJsonData(event.target.value)}
											placeholder="Enter JSON data to test"
											rows={20}
											fontFamily="monospace"
											fontSize="14px"
											resize="vertical"
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Custom Epoch Time (optional)</FormLabel>
										<Input
											type="number"
											value={customEpochTime}
											onChange={(event) => setCustomEpochTime(event.target.value)}
											placeholder={`Default: ${Math.floor(Date.now() / 1000)} (current time)`}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>GMT Offset (optional)</FormLabel>
										<Input
											type="number"
											value={customGmtOffset}
											onChange={(event) => setCustomGmtOffset(event.target.value)}
											placeholder="Default: 0 (UTC)"
										/>
									</FormControl>
								</>
							)}
						</Stack>
					</ModalBody>
					<ModalFooter>
						<HStack spacing={3}>
							<Button
								colorScheme="blue"
								isLoading={isTestingJsonFilter}
								loadingText="Testing..."
								onClick={async () => {
									if (!jsonSegmentFilter.SelectorUid) {
										toast({
											title: 'Error',
											description: 'Please select a valid selector to test the JSON filter.',
											status: 'error',
											duration: 5000,
											isClosable: true,
											position: 'top',
										});
										return;
									}
									if (jsonSegmentFilter.QueryValues.some((q) => q.Value === null || q.Value === '')) {
										toast({
											title: 'Error',
											description: 'Please fill out all filter input values.',
											status: 'error',
											duration: 5000,
											isClosable: true,
											position: 'top',
										});
										return;
									}
									if (testMode === 'playfab') {
										if (!playfabId) {
											toast({
												title: 'Error',
												description: 'Please enter a Playfab ID to test the JSON filter.',
												status: 'error',
												duration: 5000,
												isClosable: true,
												position: 'top',
											});
											return;
										}
										console.log('Test Segment', jsonSegmentFilter);
										setIsTestingJsonFilter(true);
										try {
											const url = `${config.CLOUDFLARE_LOCAL_ENDPOINT}/game/${globalData.gameId}/segment/test_jsonata`;
											const response = await axios.post(
												url,
												{
													PlayfabId: playfabId,
													Filter: jsonSegmentFilter,
												},
												{ headers: { Authorization: 'Bearer ' + globalData.JWTToken } },
											);
											console.log('Test Response:', response);
											console.log('Test Result:', response.data.evalResult);
											setJsonFilterResult(response.data.evalResult === true);
										} catch (error: any) {
											console.error('Error testing JSON filter:', error);
											toast({
												title: 'Error Testing JSON Filter',
												description: error.response?.data.error || 'An error occurred while testing the JSON filter.',
												status: 'error',
												duration: 5000,
												isClosable: true,
												position: 'top',
											});
											setJsonFilterResult(null);
										} finally {
											setIsTestingJsonFilter(false);
										}
									} else {
										// Test with JSON data directly on frontend

										if (!testJsonData.trim()) {
											toast({
												title: 'Error',
												description: 'Please enter JSON data to test the filter.',
												status: 'error',
												duration: 5000,
												isClosable: true,
												position: 'top',
											});
											return;
										}
										try {
											// Parse and validate JSON
											const parsedJsonData = JSON.parse(testJsonData);

											// Use custom values if provided, otherwise use defaults
											const epochTime = customEpochTime ? parseInt(customEpochTime) : Math.floor(Date.now() / 1000);
											const gmtOffset = customGmtOffset ? parseInt(customGmtOffset) : 0;

											// Test the JSONata query with the provided JSON data
											const result = await calculateJsonAtaQueryResult(jsonSegmentFilter, parsedJsonData, epochTime, gmtOffset);

											setJsonFilterResult(!!result);
										} catch (error: any) {
											// Check if it's a JSON parse error
											if (error instanceof SyntaxError) {
												toast({
													title: 'Invalid JSON',
													description: 'The provided JSON data is not valid. Please check the format.',
													status: 'error',
													duration: 5000,
													isClosable: true,
													position: 'top',
												});
											} else {
												// Handle other errors (JSONata evaluation errors, etc.)
												console.error('Error testing JSON filter with custom data:', error);
												toast({
													title: 'Error Testing JSON Filter',
													description: error.message || 'An error occurred while testing the JSON filter.',
													status: 'error',
													duration: 5000,
													isClosable: true,
													position: 'top',
												});
											}
											setJsonFilterResult(null);
										}
									}
								}}
							>
								Test User Query
							</Button>
							<Button
								colorScheme="blue"
								onClick={() => {
									setPlayfabId('');
									// setTestJsonData('');
									setCustomEpochTime('');
									setCustomGmtOffset('');
									setTestMode('playfab');

									/* 	setJsonSegmentFilter({
										Filter: FilterType.JSON,
										SelectorUid: '',
										QueryValues: [],
										Query: '',
									}); */
									setJsonFilterResult(null);
									setIsJsonFilterModalOpen(false);
								}}
							>
								Close
							</Button>
						</HStack>
					</ModalFooter>
					{jsonFilterResult !== null && (
						<Alert
							status={jsonFilterResult ? 'success' : 'error'}
							mt={4}
							variant="subtle"
							borderRadius="md"
							alignItems="center"
							textAlign="center"
						>
							<AlertIcon boxSize="20px" />
							<AlertTitle>{jsonFilterResult ? 'Test Passed' : 'Test Failed'}</AlertTitle>
						</Alert>
					)}
				</ModalContent>
			</Modal>
		</Modal>
	);
}

export default SegmentModal;
